import { Stack, Typography, useTheme } from "@mui/material";
import type { FC } from "react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { BoxedSliderField } from "web_myplann/Inspiration/Components/BoxedSliderField";
import { InspirationSection } from "web_myplann/Inspiration/Components/InspirationSection";
import { InspirationToneNote } from "web_myplann/Inspiration/Components/InspirationToneNote";
import { RetirementScenarioList } from "web_myplann/Inspiration/Retirement/Components/RetirementScenarioList";
import {
	RETIREMENT_CONTRIBUTION_MIN,
	RETIREMENT_CONTRIBUTION_STEP,
} from "web_myplann/Inspiration/Retirement/constants";
import { mapRetirementPaceFeedback } from "web_myplann/Inspiration/Retirement/Mappers/mapRetirementPaceFeedback";
import type {
	RetirementContext,
	RetirementScenarioKey,
} from "web_myplann/Inspiration/Retirement/Types/RetirementTypes";
import { formatInspirationMoney } from "web_myplann/Inspiration/Utils/inspirationMoney";

const R = Resources.Inspiration.Retirement;

type Props = {
	context: RetirementContext;
	onChangeContribution: (value: number) => void;
	onSelectScenario: (key: RetirementScenarioKey) => void;
};

export const RetirementPaceSection: FC<Props> = ({
	context,
	onChangeContribution,
	onSelectScenario,
}) => {
	const { t } = useResource();
	const theme = useTheme();
	const { color, icon, text, values } = mapRetirementPaceFeedback(context);

	return (
		<InspirationSection title={t(R.paceTitle)} gap={theme.spacingTokens.md}>
			<BoxedSliderField
				label={t(R.paceSliderLabel)}
				value={context.monthlyContribution}
				onChange={onChangeContribution}
				min={RETIREMENT_CONTRIBUTION_MIN}
				max={context.contributionMax}
				step={RETIREMENT_CONTRIBUTION_STEP}
				hint={
					context.hasIncome
						? t(R.paceHint, {
								amount: formatInspirationMoney(context.freeCashflow),
							})
						: undefined
				}
			/>

			<RetirementScenarioList
				scenarios={context.scenarios}
				selected={context.selectedScenario}
				onSelect={onSelectScenario}
			/>

			<Stack gap={theme.spacingTokens.sm}>
				<InspirationToneNote
					color={color}
					icon={icon}
					text={text}
					values={values}
				/>
				<Typography variant="hint" color={theme.palette.grey[500]} relaxed>
					{t(R.paceNote)}
				</Typography>
			</Stack>
		</InspirationSection>
	);
};
